import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@cleverly/shared/api-authorization';
import { filter, Subject, switchMap, take } from 'rxjs';

@Component({
    selector: 'app-verify-login',
    templateUrl: './verify-login.component.html',
    styleUrls: [],
    host: { class: 'flex align-items-center h-full' },
    standalone: false
})
export class VerifyLoginComponent implements OnInit, OnDestroy {
    private _authService: AuthService;
    private _router: Router;
    private _route: ActivatedRoute;
    private unsubscribeReceiver: Subject<void> = new Subject();
    constructor(authService: AuthService, router: Router, route: ActivatedRoute) {
        this._authService = authService;
        this._router = router;
        this._route = route;
    }

    ngOnInit() {
        const token = this._route.snapshot.queryParamMap.get('emailToken');
        const returnUrl = this._route.snapshot.queryParamMap.get('returnUrl') || '/';

        if (!token) {
            this._router.navigate(['/login']);
            return;
        }

        // Verify email login and wait for Firebase auth state to propagate
        // This prevents race conditions where navigation occurs before the auth token is available
        // The flow: verify token → wait for Firebase custom token login → wait for the new
        // session's roles to be resolved → navigate. Waiting for roles (not just any access
        // token) ensures the RoleGuard evaluates the destination route against the freshly
        // signed-in user's claims instead of a previously cached session's roles.
        this._authService.verifyEmailLogin(token)
            .pipe(
                // Wait until the roles of the newly signed-in user have been resolved from the
                // refreshed ID token. loginWithCustomToken signs the previous user out first,
                // which clears roles to [], so a non-empty value guarantees the new token applied.
                switchMap(() => this._authService.roles$),
                filter(roles => !!roles && roles.length > 0),
                take(1) // Complete after roles are resolved - prevents takeUntil from cancelling
            )
            .subscribe({
                next: () => {
                    // Use Angular Router for SPA navigation - preserves app state and Firebase auth
                    const urlTree = this._router.parseUrl(decodeURIComponent(returnUrl));
                    
                    // Navigate using Angular Router to maintain application state
                    // This ensures the Firebase token we just waited for remains available
                    this._router.navigateByUrl(urlTree);
                },
                error: (error) => {
                    console.error('Email login verification failed:', error);
                    this._router.navigate(['/login'], {
                        queryParams: { error: 'verification_failed' }
                    });
                },
            });
    }
    ngOnDestroy(): void {
        this.unsubscribeReceiver.next();
        this.unsubscribeReceiver.complete();
    }
}
